/**
 * LiveGexEmbed — the Real-Time GEX workspace alone, full viewport, no app shell.
 *
 * Mounted instead of <App/> when the page is opened with ?view=live-gex (see index.tsx).
 * Used for clean captures (the Telegram posting bots) and for embedding. Same session
 * model as MobileChartEmbed: the JWT is read from storage ('authToken') and promoted into
 * the in-memory slot the global fetch wrapper serves.
 *
 * Query params:
 *   symbol   SPX | NDX | SPY | QQQ | IWM (default SPX)
 */
import React, { useState } from "react";
import LiveGexWorkspace from "./components/livegex/LiveGexWorkspace";
import { setAuthTokenInMemory } from "./utils/authUtils";

function readStoredToken(): string {
    try {
        return window.localStorage.getItem("authToken") || window.sessionStorage.getItem("authToken") || "";
    } catch {
        return "";
    }
}

export default function LiveGexEmbed() {
    const q = new URLSearchParams(window.location.search);
    const symbol = (q.get("symbol") || "SPX").toUpperCase();
    // Capture options: ?range=week &levels=session &ta=1 &marks=TGT:243.9:facc15,+5d:247.4:94a3b8
    const range = q.get("range") === "week" ? "week" : "0dte";
    const marks = (q.get("marks") || "")
        .split(",")
        .map((m) => m.split(":"))
        .filter((p) => p.length >= 2 && Number(p[1]) > 0)
        .map((p) => ({ label: p[0].slice(0, 12), price: Number(p[1]), color: /^[0-9a-fA-F]{6}$/.test(p[2] || "") ? `#${p[2]}` : "#facc15" }));
    const [hasToken] = useState<boolean>(() => {
        const tok = readStoredToken();
        if (tok) setAuthTokenInMemory(tok);
        return !!tok;
    });

    if (!hasToken) {
        return (
            <div
                style={{
                    height: "100dvh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#0b0d14",
                    color: "#8a87a0",
                    fontFamily: "system-ui, sans-serif",
                    fontSize: 13,
                }}
            >
                No session — sign in to the platform to view Real-Time GEX.
            </div>
        );
    }

    return (
        <div style={{ height: "100dvh", width: "100vw", overflow: "hidden", background: "#0b0d14" }}>
            <LiveGexWorkspace
                symbol={symbol}
                fill
                initialRange={range as any}
                sessionLevels={q.get("levels") === "session"}
                ta={q.get("ta") === "1"}
                marks={marks}
            />
        </div>
    );
}
